import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@monksflow/monks-ui';
import { SparklesIcon } from 'lucide-react';

import type { TrackerCellMeta } from '@/entities/tracker-automation';

/**
 * A cell whose value an AI action wrote, marked as such with the model and
 * prompt version behind it. The mark goes as soon as anyone writes the cell.
 */
export function AiCellMarker({ meta, children }: { meta: TrackerCellMeta; children: ReactNode }) {
  const { t } = useTranslation('trackers');
  const label = t('trackersBoard.automationAi.cell.writtenBy', {
    model: meta.model,
    promptVersion: meta.promptVersion,
  });
  return (
    <div className="relative">
      {children}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              role="img"
              aria-label={label}
              // Focusable so the tooltip, the only place the model is named, opens from the keyboard.
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
              tabIndex={0}
              className="pointer-events-auto absolute right-1 top-1 rounded-sm text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <SparklesIcon className="size-3" aria-hidden />
            </span>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
